import { useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";

import { Button } from "elements/button/Button";
import { COLORS } from "models/colors";
import { VARIANTS } from "models/variants";
import { Input } from "elements/input/Input";
import { validators } from "validators";
import { useAxios } from "providers/axios";
import { useCognito } from "providers/cognito";
import { usePayment } from "hooks/use-payment";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "routes";
import AccountDetailsPageSections from "components/account-details/AccountDetailsPageSections";
import {
  clearGiftCardPurchase,
  getPendingGiftCardPurchase,
  PendingGiftCardPurchase,
  saveGiftCardPurchase
} from "utils/giftCardPurchaseRequest";

import "./GiftCardPurchase.scss";

// The buy-a-gift-card form itself, with no page or modal chrome around it.
//
// Kept separate so it can be shown two ways: in a modal over the salon page
// (the normal route in), and on its own page at /company/:slug/gift-card for
// links people share or come back to from an email.
//
// Two steps: choose the amount and who it is for, then pay with a card already
// on file. Login is required upstream, so there is always a buyer email and a
// Stripe customer to charge.

const QUICK_AMOUNTS = [25, 50, 100, 150];
const MIN_VALUE = 1;
const MAX_VALUE = 1000;

type Step = "details" | "payment";

interface IGiftCardPurchaseProps {
  companyId?: string;
  companyName?: string | null;
  userPaysFees?: boolean | null;
  recoveryOnly?: boolean;
  /** Called after a successful purchase, so a modal can close itself. */
  onDone?: () => void;
}

export const GiftCardPurchase = (props: IGiftCardPurchaseProps) => {
  const { user } = useCognito();
  return (
    <GiftCardPurchaseForm
      key={`${user?.id}:${props.companyId}`}
      {...props}
      userId={user?.id}
    />
  );
};

const GiftCardPurchaseForm = ({
  companyId,
  companyName,
  userPaysFees,
  recoveryOnly,
  userId,
  onDone
}: IGiftCardPurchaseProps & { userId?: string }) => {
  const { api } = useAxios();
  const { cards } = usePayment();
  const navigate = useNavigate();

  const [initial] = useState(() => {
    try {
      return {
        pending:
          userId && companyId
            ? getPendingGiftCardPurchase(userId, companyId)
            : null,
        error: null as string | null
      };
    } catch (err: any) {
      return { pending: null, error: err.message as string };
    }
  });
  const [pending, setPending] = useState(initial.pending);
  const [step, setStep] = useState<Step>(
    initial.pending ? "payment" : "details"
  );
  const [amount, setAmount] = useState(
    String(initial.pending?.request.value || 50)
  );
  const [forSomeoneElse, setForSomeoneElse] = useState(
    initial.pending ? !!initial.pending.request.recipient_email : true
  );
  const [recipientEmail, setRecipientEmail] = useState(
    initial.pending?.request.recipient_email || ""
  );
  // The error waits for the field to lose focus, so it does not show while
  // someone is still typing the address.
  const [recipientEmailTouched, setRecipientEmailTouched] = useState(false);
  const [recipientName, setRecipientName] = useState(
    initial.pending?.request.recipient_name || ""
  );
  const [message, setMessage] = useState(
    initial.pending?.request.message || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const [purchased, setPurchased] = useState<{
    formatted_code: string;
    request: PendingGiftCardPurchase["request"];
  } | null>(null);

  const value = pending?.request.value ?? Number(amount);
  const valueIsValid =
    Number.isFinite(value) &&
    value >= MIN_VALUE &&
    value <= MAX_VALUE &&
    Math.abs(value * 100 - Math.round(value * 100)) < 0.000001;

  // The 4% appears only where the salon passes processing fees to the
  // customer. Shown before payment so the total is never a surprise.
  const fees =
    pending?.fees ??
    (userPaysFees && valueIsValid ? Number((value * 0.04).toFixed(2)) : 0);
  const total = valueIsValid ? Number((value + fees).toFixed(2)) : 0;

  const card = useMemo(
    () => (cards || []).find((c: any) => c.is_default) || (cards || [])[0],
    [cards]
  );

  // Same rule as sign-up. Without it any text, a name included, got as far as
  // payment, where the server refused it with a generic error.
  const recipientEmailError = recipientEmail.trim()
    ? validators.email()(recipientEmail.trim())
    : null;

  const canContinue =
    valueIsValid &&
    (!forSomeoneElse ||
      (recipientEmail.trim().length > 0 &&
        !recipientEmailError &&
        recipientName.length <= 200));

  const purchase = async () => {
    if (inFlight.current || !userId || !companyId) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    let attempt = pending;
    try {
      if (!attempt) {
        if (recoveryOnly || !canContinue) return;
        const paymentMethodId = card?.object_json?.id;
        if (!paymentMethodId) {
          throw new Error(
            "Add a card to your account before buying a gift card."
          );
        }
        attempt = saveGiftCardPurchase(
          userId,
          {
            company_id: companyId,
            value,
            payment_method_id: paymentMethodId,
            recipient_email: forSomeoneElse ? recipientEmail.trim() : undefined,
            recipient_name: forSomeoneElse
              ? recipientName.trim() || undefined
              : undefined,
            message: forSomeoneElse ? message.trim() || undefined : undefined
          },
          { fees, cardLabel: `${card.brand} ending ${card.last4}` }
        );
        setPending(attempt);
      }
      let response = await api.post("/v1/gift-cards/purchase", attempt.request);
      if (response.data.purchase_status === "REQUIRES_ACTION") {
        const { client_secret, stripe_account } = response.data;
        if (!client_secret || !stripe_account) {
          throw new Error(
            "We could not verify this payment. Check your purchase again."
          );
        }
        const stripe = await loadStripe(
          process.env.REACT_APP_STRIPE_PUBLIC_KEY || "",
          { stripeAccount: stripe_account }
        );
        if (!stripe)
          throw new Error(
            "Payment verification is unavailable. Please check your purchase again."
          );
        // The backend observes the payment after authentication, including when
        // Stripe's browser response is lost or the customer closes its dialog.
        await stripe.confirmCardPayment(client_secret);
        response = await api.post("/v1/gift-cards/purchase", attempt.request);
      }
      if (response.data.purchase_status !== "COMPLETED") {
        setError(
          response.data.message ||
            "Your payment is still processing. Check your purchase again shortly."
        );
        return;
      }
      if (!response.data.formatted_code) {
        throw new Error(
          "We could not confirm your gift card. Check your purchase again."
        );
      }

      clearGiftCardPurchase(
        userId,
        companyId,
        attempt.request.purchase_request_id
      );
      setPurchased({ ...response.data, request: attempt.request });
      setPending(null);
    } catch (err: any) {
      if (attempt && err?.response?.data?.purchase_retry_safe === true) {
        clearGiftCardPurchase(
          userId,
          companyId,
          attempt.request.purchase_request_id
        );
        setPending(null);
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "We could not confirm your purchase. Check it again to recover the same purchase."
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (purchased) {
    return (
      <div className="gift-card-purchase">
        <p className="gift-card-purchase__done-title">
          Your gift card is on its way
        </p>
        <p className="gift-card-purchase__hint">
          {purchased.request.recipient_email
            ? `We've emailed it to ${purchased.request.recipient_email}, and sent your receipt.`
            : "We've emailed the card and your receipt."}
        </p>
        <div className="gift-card-purchase__code-card">
          <span className="gift-card-purchase__code-value">
            ${purchased.request.value.toFixed(2)}
          </span>
          <span className="gift-card-purchase__code">
            {purchased.formatted_code}
          </span>
        </div>
        <Button
          color={COLORS.PRIMARY}
          variant={VARIANTS.FILLED}
          text="Done"
          stretch
          className="flexible"
          onClick={() => onDone?.()}
        />
      </div>
    );
  }

  if (initial.error || (recoveryOnly && !pending)) {
    return (
      <div className="gift-card-purchase">
        <p className="gift-card-purchase__error" role="alert">
          {initial.error ||
            error ||
            "This salon is not selling gift cards online at the moment."}
        </p>
      </div>
    );
  }

  if (step === "details") {
    return (
      <div className="gift-card-purchase">
        {/* Quick picks are a shortcut - the field below still takes any amount
            between the minimum and maximum, and carries the label. */}
        <div className="gift-card-purchase__amounts">
          {QUICK_AMOUNTS.map((quick) => (
            <button
              key={quick}
              type="button"
              className={`gift-card-purchase__chip ${
                String(quick) === amount ? "gift-card-purchase__chip--on" : ""
              }`}
              onClick={() => setAmount(String(quick))}
            >
              ${quick}
            </button>
          ))}
        </div>
        <Input
          type="number"
          label="Amount"
          value={amount}
          onChange={(next) => setAmount(String(next ?? ""))}
          withoutErrorAndHint
        />
        <p className="gift-card-purchase__hint">
          Use the preset amount, or enter a custom amount.
        </p>

        <span className="gift-card-purchase__label">
          Who should we send this to?
        </span>
        <div className="gift-card-purchase__amounts">
          <button
            type="button"
            className={`gift-card-purchase__chip ${
              forSomeoneElse ? "gift-card-purchase__chip--on" : ""
            }`}
            onClick={() => setForSomeoneElse(true)}
          >
            Someone else
          </button>
          <button
            type="button"
            className={`gift-card-purchase__chip ${
              !forSomeoneElse ? "gift-card-purchase__chip--on" : ""
            }`}
            onClick={() => setForSomeoneElse(false)}
          >
            Myself
          </button>
        </div>

        {forSomeoneElse && (
          <>
            <Input
              label="Recipient name (optional)"
              value={recipientName}
              onChange={(next) => setRecipientName(String(next ?? ""))}
              errorMsg={
                recipientName?.length > 200
                  ? "Use 200 characters or fewer."
                  : ""
              }
              withoutErrorAndHint={recipientName?.length <= 200}
            />
            <Input
              label="Personalized message (optional)"
              value={message}
              onChange={(next) => setMessage(String(next ?? ""))}
              withoutErrorAndHint={true}
            />
            <Input
              type="email"
              label="Recipient email"
              value={recipientEmail}
              onChange={(next) => setRecipientEmail(String(next ?? ""))}
              onBlur={() => setRecipientEmailTouched(true)}
              errorMsg={recipientEmailTouched ? recipientEmailError || "" : ""}
              hint="We'll email them the card and your message."
            />
          </>
        )}

        <Button
          color={COLORS.PRIMARY}
          variant={VARIANTS.FILLED}
          text="Continue to payment"
          stretch
          className="flexible"
          disabled={!canContinue}
          onClick={() => setStep("payment")}
        />
      </div>
    );
  }

  return (
    <div className="gift-card-purchase">
      <span className="gift-card-purchase__label">Pay with</span>
      {pending || card ? (
        <div className="gift-card-purchase__card">
          {pending?.cardLabel || `${card.brand} ending ${card.last4}`}
        </div>
      ) : (
        // Dead end otherwise: the Pay button below is disabled without a card,
        // and nothing on this screen said where to add one. Sends them straight
        // to the payment methods tab on their account.
        <div className="gift-card-purchase__no-card">
          <p className="gift-card-purchase__hint">
            You need a card on your account before buying a gift card.
          </p>
          <Button
            color={COLORS.SECONDARY}
            variant={VARIANTS.OUTLINED}
            text="Add a payment method"
            iconLeftName="plus"
            stretch
            className="flexible"
            onClick={() => {
              // Closes the modal first when this is shown in one, so coming
              // back with the browser's back button does not land on a salon
              // page with a stale dialog over it.
              onDone?.();
              navigate(
                `${APP_ROUTES.ACCOUNT_DETAILS}${AccountDetailsPageSections.PAYMENT_METHODS}`
              );
            }}
          />
        </div>
      )}

      <div className="gift-card-purchase__totals">
        <div className="gift-card-purchase__row">
          <span>Gift card value</span>
          <span>${value.toFixed(2)}</span>
        </div>
        {fees > 0 && (
          <div className="gift-card-purchase__row">
            <span>Fees (4%)</span>
            <span>${fees.toFixed(2)}</span>
          </div>
        )}
        <div className="gift-card-purchase__row gift-card-purchase__row--total">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Stated before payment, not after. A gift card purchase can never be
          reversed, and meeting that fact afterwards becomes a dispute. */}
      <p className="gift-card-purchase__hint">
        The recipient gets ${value.toFixed(2)} to spend at{" "}
        {companyName || "this salon"}. Gift cards don&apos;t expire and
        can&apos;t be refunded.
      </p>

      {pending && (
        <p className="gift-card-purchase__hint">
          Your purchase is saved. Check it to continue the same payment and
          recover your gift card.
          {pending.request.recipient_email &&
            ` Recipient: ${pending.request.recipient_email}.`}
        </p>
      )}
      {error && (
        <p className="gift-card-purchase__error" role="alert">
          {error}
        </p>
      )}

      <Button
        color={COLORS.PRIMARY}
        variant={VARIANTS.FILLED}
        text={pending ? "Check purchase" : `Pay $${total.toFixed(2)}`}
        stretch
        className="flexible"
        submitting={submitting}
        disabled={submitting || !userId || !companyId || (!pending && !card)}
        onClick={purchase}
      />
      <Button
        color={COLORS.PRIMARY}
        variant={VARIANTS.OUTLINED}
        text="Back"
        stretch
        className="flexible"
        disabled={submitting || !!pending}
        onClick={() => setStep("details")}
      />
    </div>
  );
};

export default GiftCardPurchase;
